import { z } from "zod";
import { fontWeights } from "./brand-kit";
import { zExt } from "./common/zod-util";
import {
  ButtonElementSchema,
  ColumnElementSchema,
  ImageElementSchema,
  InputElementSchema,
  RichTextElementSchema,
} from "./content-builder/element-schema";
import { FONT_FAMILIES } from "./email-builder";
import {
  alignmentHorizontalValues,
  alignmentVerticalValues,
  audienceTypes,
  deviceOptions,
  directions,
  LayoutType,
  SignUpFormGoal,
  signupFormSiteTypes,
  targetingTypes,
  teaserVisibility,
  URLVisibilityType,
} from "./signup-form";

const BorderStrokeStyleDefinitionSchema_ = z.object({
  color: z.string(),
  weightPx: z.number(),
});
const BorderStrokeStyleDefinitionSchema: z.ZodType<BorderStrokeStyleDefinition> =
  BorderStrokeStyleDefinitionSchema_;
export interface BorderStrokeStyleDefinition
  extends z.infer<typeof BorderStrokeStyleDefinitionSchema_> {}

const BorderStyleDefinitionSchema_ = z.object({
  cornerRadiusPx: z.number(),
  stroke: BorderStrokeStyleDefinitionSchema,
});
const BorderStyleDefinitionSchema: z.ZodType<BorderStyleDefinition> =
  BorderStyleDefinitionSchema_;
export interface BorderStyleDefinition
  extends z.infer<typeof BorderStyleDefinitionSchema_> {}

const elementStyleSchema_ = z.object({
  font: z.object({
    fontFamily: z.enum(FONT_FAMILIES),
    fontWeight: z.enum(fontWeights),
    fontSizePx: z.number(),
  }),
  textColor: z.string(),
  backgroundColor: z.optional(z.string()),
  border: z.object({
    cornerRadiusPx: z.number(),
    stroke: BorderStrokeStyleDefinitionSchema,
  }),
  paddingPx: z.number(),
});
const elementStyleSchema: z.ZodType<ElementStyle> = elementStyleSchema_;
export interface ElementStyle extends z.infer<typeof elementStyleSchema_> {}

const inputStyleSchema = elementStyleSchema_.extend({ errorColor: z.string() });

const buttonStyleSchema = elementStyleSchema;

const TeaserSchema_ = z.object({
  enabled: z.boolean(),
  text: z.string(),
  fontFamily: z.enum(FONT_FAMILIES),
  fontWeight: z.enum(fontWeights),
  fontSizePx: z.number(),
  positionHorizontal: z.enum(alignmentHorizontalValues),
  positionVertical: z.enum(alignmentVerticalValues),
  backgroundColor: z.string(),
  textColor: z.string(),
  cornerRadiusPx: z.number(),
  borderStroke: BorderStrokeStyleDefinitionSchema,
  iconVisible: z.boolean(),
  visibility: z.enum(teaserVisibility),
});
const TeaserSchema: z.ZodType<Teaser> = TeaserSchema_;
export interface Teaser extends z.infer<typeof TeaserSchema_> {}

const ElementSchema = z.union([
  RichTextElementSchema,
  ButtonElementSchema,
  InputElementSchema,
  ImageElementSchema,
  ColumnElementSchema,
]);

const FormStepSchema_ = z.object({ elements: z.array(ElementSchema) });
const FormStepSchema: z.ZodType<FormStep> = FormStepSchema_;
export interface FormStep extends z.infer<typeof FormStepSchema_> {}

const signupFormSchema_ = z.object({
  name: z.string(),
  goal: z.enum(SignUpFormGoal),
  theme: z.object({
    overrides: z.object({
      buttons: z.object({
        enabled: z.boolean(),
        primary: buttonStyleSchema,
        secondary: buttonStyleSchema,
        tertiary: buttonStyleSchema,
      }),
      inputs: inputStyleSchema.extend({ enabled: z.boolean() }),
    }),
    border: BorderStyleDefinitionSchema,
    closeButton: z.object({
      color: z.string(),
      background: z.object({ enabled: z.boolean(), color: z.string() }),
    }),
    overlay: z.object({ color: z.string(), opacityPercent: z.number() }),
  }),
  layout: z.object({
    type: z.enum(LayoutType),
    backgroundColor: z.string(),
    image: z.object({
      style: z.enum(directions),
      url: z.string(),
      hideOnMobile: z.boolean(),
    }),
  }),
  discount: z.object({
    enabled: z.boolean(),
    discountId: z.string().optional(),
  }),
  behavior: z.object({
    appearsOn: z.enum(deviceOptions),
    appearsOnSite: z.enum(signupFormSiteTypes),
    subscriberTags: z.array(z.string()),
    display: z.object({
      timeOnPage: z.object({ enabled: z.boolean(), seconds: z.number() }),
      exitIntent: z.object({ enabled: z.boolean() }),
    }),
    audience: z.enum(audienceTypes),
    campaignUtms: z.array(z.string()),
    targeting: z.enum(targetingTypes),
    embeddedCode: z.string(),
    urlVisibility: z.object({
      type: z.enum(URLVisibilityType),
      urls: z.array(z.string()),
    }),
  }),
  teaser: TeaserSchema,
  pdpButton: ButtonElementSchema.optional(),
  steps: z.array(FormStepSchema),
});
export const signupFormSchema: z.ZodType<SignupForm> = signupFormSchema_;
export interface SignupForm extends z.infer<typeof signupFormSchema_> {}

const signupFormWithAnalyticsSchema_ = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  form: signupFormSchema,
  analytics: z.object({ views: z.number(), submissions: z.number() }),
  publishedAt: z.date().nullable(),
});
export const signupFormWithAnalyticsSchema: z.ZodType<SignupFormWithAnalytics> =
  signupFormWithAnalyticsSchema_;
export interface SignupFormWithAnalytics
  extends z.infer<typeof signupFormWithAnalyticsSchema_> {}
